import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  LogOut, Search, Phone, Mail, Trash2, Inbox, Clock3, Loader2, CheckCircle2, Ban, X, Plus, MessageSquare, Calendar,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Logo } from "../components/Logo";
import { STATUS_OPTIONS } from "../data/content";

function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(email, password);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Sikertelen bejelentkezés");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 ss-grain px-5">
      <div className="absolute top-0 left-1/3 w-[500px] h-[500px] rounded-full bg-sky-600/20 blur-[130px]" />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl p-8"
      >
        <div className="flex flex-col items-center mb-7">
          <Logo className="w-20 h-20" light />
          <h1 className="mt-4 font-display text-2xl font-extrabold text-slate-900">Admin belépés</h1>
          <p className="text-sm text-slate-500">Szuper Szaki – lead kezelő</p>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-sm font-bold text-slate-700 mb-1.5">E-mail cím</label>
            <input
              data-testid="admin-login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
              placeholder="admin@szuperszaki.hu"
            />
          </div>
          <div>
            <label className="block text-sm font-bold text-slate-700 mb-1.5">Jelszó</label>
            <input
              data-testid="admin-login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
              placeholder="••••••••"
            />
          </div>
          {error && <p className="text-sm text-red-600 font-semibold" data-testid="admin-login-error">{error}</p>}
          <button
            data-testid="admin-login-submit"
            type="submit"
            disabled={loading}
            className="w-full bg-sky-600 hover:bg-sky-500 disabled:opacity-60 text-white font-bold py-3.5 rounded-xl transition-colors inline-flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Belépés"}
          </button>
        </form>
        <Link to="/" className="block text-center mt-6 text-sm text-slate-400 hover:text-sky-600">← Vissza a weboldalra</Link>
      </motion.div>
    </div>
  );
}

const statusMeta = (id) => STATUS_OPTIONS.find((s) => s.id === id) || STATUS_OPTIONS[0];

function Dashboard() {
  const { user, logout } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("összes");
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/leads");
      setLeads(data);
    } catch {
      toast.error("Nem sikerült betölteni a leadeket");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const counts = useMemo(() => {
    const c = { összes: leads.length };
    STATUS_OPTIONS.forEach((s) => (c[s.id] = leads.filter((l) => l.status === s.id).length));
    return c;
  }, [leads]);

  const filtered = useMemo(() => {
    let r = filter === "összes" ? leads : leads.filter((l) => l.status === filter);
    if (query.trim()) {
      const q = query.toLowerCase();
      r = r.filter(
        (l) =>
          l.name.toLowerCase().includes(q) ||
          l.phone.toLowerCase().includes(q) ||
          l.email.toLowerCase().includes(q) ||
          (l.message || "").toLowerCase().includes(q)
      );
    }
    return r;
  }, [leads, filter, query]);

  const changeStatus = async (lead, status) => {
    setLeads((prev) => prev.map((l) => (l.id === lead.id ? { ...l, status } : l)));
    setActive((a) => (a && a.id === lead.id ? { ...a, status } : a));
    try {
      await api.patch(`/leads/${lead.id}`, { status });
      toast.success("Státusz frissítve");
    } catch {
      toast.error("Nem sikerült frissíteni");
      load();
    }
  };

  const saveNote = async (lead, admin_note) => {
    try {
      await api.patch(`/leads/${lead.id}`, { admin_note });
      setLeads((prev) => prev.map((l) => (l.id === lead.id ? { ...l, admin_note } : l)));
      toast.success("Megjegyzés mentve");
    } catch {
      toast.error("Nem sikerült menteni");
    }
  };

  const remove = async (lead) => {
    if (!window.confirm(`Biztosan törli "${lead.name}" leadjét?`)) return;
    try {
      await api.delete(`/leads/${lead.id}`);
      setLeads((prev) => prev.filter((l) => l.id !== lead.id));
      setActive(null);
      toast.success("Lead törölve");
    } catch {
      toast.error("Nem sikerült törölni");
    }
  };

  const metrics = [
    { label: "Összes lead", value: counts["összes"], icon: Inbox, color: "text-sky-600 bg-sky-50" },
    { label: "Új megkeresés", value: counts["új"], icon: Clock3, color: "text-blue-600 bg-blue-50" },
    { label: "Folyamatban", value: counts["folyamatban"], icon: Loader2, color: "text-purple-600 bg-purple-50" },
    { label: "Lezárt munka", value: counts["lezárva"], icon: CheckCircle2, color: "text-emerald-600 bg-emerald-50" },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <span className="w-10 h-10 rounded-xl bg-white ring-1 ring-slate-200 p-0.5 flex items-center justify-center">
              <Logo className="w-8 h-8" />
            </span>
            <div className="leading-none">
              <div className="font-display font-extrabold text-slate-900">Lead vezérlőpult</div>
              <div className="text-xs text-slate-400">{user?.name}</div>
            </div>
          </Link>
          <button
            data-testid="admin-logout"
            onClick={logout}
            className="inline-flex items-center gap-2 text-slate-600 hover:text-red-600 font-semibold text-sm px-4 py-2 rounded-full hover:bg-red-50 transition-colors"
          >
            <LogOut className="w-4 h-4" /> Kilépés
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-8">
        {/* metrics */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {metrics.map((m) => (
            <div key={m.label} className="bg-white rounded-2xl border border-slate-200 p-5">
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-3 ${m.color}`}>
                <m.icon className="w-5 h-5" />
              </div>
              <div className="font-display text-3xl font-extrabold text-slate-900">{m.value}</div>
              <div className="text-sm text-slate-500">{m.label}</div>
            </div>
          ))}
        </div>

        {/* filters */}
        <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="w-5 h-5 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input
              data-testid="admin-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Keresés név, telefon, e-mail vagy probléma szerint…"
              className="w-full rounded-full border border-slate-200 bg-white pl-11 pr-4 py-2.5 outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
            />
          </div>
          <div className="flex flex-wrap gap-2" data-testid="admin-status-filter">
            {[{ id: "összes", label: "Összes" }, ...STATUS_OPTIONS].map((s) => (
              <button
                key={s.id}
                onClick={() => setFilter(s.id)}
                className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
                  filter === s.id ? "bg-sky-600 text-white border-sky-600" : "bg-white text-slate-600 border-slate-200 hover:border-sky-300"
                }`}
              >
                {s.label} {counts[s.id] != null && <span className="opacity-70">({counts[s.id]})</span>}
              </button>
            ))}
          </div>
        </div>

        {/* list */}
        {loading ? (
          <div className="text-center py-20 text-slate-400"><Loader2 className="w-8 h-8 animate-spin mx-auto" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-2xl border border-dashed border-slate-300">
            <Inbox className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 font-semibold">Nincs megjeleníthető lead.</p>
          </div>
        ) : (
          <div className="grid gap-3">
            {filtered.map((lead) => {
              const sm = statusMeta(lead.status);
              return (
                <motion.div
                  key={lead.id}
                  data-testid="admin-lead-card"
                  layout
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-2xl border border-slate-200 p-5 hover:shadow-md transition-shadow"
                >
                  <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                    <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setActive(lead)}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full border ${sm.color}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${sm.dot}`} /> {sm.label}
                        </span>
                        {lead.source === "telefon" && <span className="text-xs text-slate-400 font-semibold">telefonos</span>}
                        <span className="text-xs text-slate-400 flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(lead.created_at).toLocaleDateString("hu-HU", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                      </div>
                      <div className="font-display font-bold text-lg text-slate-900 mt-1.5">{lead.name}</div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-500 mt-0.5">
                        <a href={`tel:${lead.phone}`} className="flex items-center gap-1 hover:text-sky-600"><Phone className="w-3.5 h-3.5" />{lead.phone}</a>
                        <a href={`mailto:${lead.email}`} className="flex items-center gap-1 hover:text-sky-600"><Mail className="w-3.5 h-3.5" />{lead.email}</a>
                      </div>
                      {lead.message && <p className="text-sm text-slate-600 mt-2 line-clamp-2">{lead.message}</p>}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <select
                        data-testid="admin-lead-status-select"
                        value={lead.status}
                        onChange={(e) => changeStatus(lead, e.target.value)}
                        className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold outline-none focus:border-sky-500 cursor-pointer"
                      >
                        {STATUS_OPTIONS.map((s) => (
                          <option key={s.id} value={s.id}>{s.label}</option>
                        ))}
                      </select>
                      <button onClick={() => remove(lead)} className="w-9 h-9 rounded-xl flex items-center justify-center text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors" title="Törlés">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {active && <LeadDrawer lead={active} onClose={() => setActive(null)} onStatus={changeStatus} onNote={saveNote} onDelete={remove} />}
    </div>
  );
}

function LeadDrawer({ lead, onClose, onStatus, onNote, onDelete }) {
  const [note, setNote] = useState(lead.admin_note || "");
  const sm = statusMeta(lead.status);
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="admin-lead-drawer">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 32 }}
        className="relative w-full max-w-md bg-white h-full overflow-y-auto shadow-2xl"
      >
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <h3 className="font-display font-extrabold text-lg text-slate-900">Lead részletek</h3>
          <button onClick={onClose} className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-slate-100"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-6">
          <div>
            <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full border ${sm.color}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${sm.dot}`} /> {sm.label}
            </span>
            <div className="font-display text-2xl font-extrabold text-slate-900 mt-3">{lead.name}</div>
            <div className="text-sm text-slate-400 mt-1">Beérkezett: {new Date(lead.created_at).toLocaleString("hu-HU")}</div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <a href={`tel:${lead.phone}`} className="flex items-center justify-center gap-2 bg-sky-600 hover:bg-sky-500 text-white font-bold py-3 rounded-xl"><Phone className="w-4 h-4" /> Hívás</a>
            <a href={`mailto:${lead.email}`} className="flex items-center justify-center gap-2 border border-slate-200 hover:bg-slate-50 text-slate-700 font-bold py-3 rounded-xl"><Mail className="w-4 h-4" /> E-mail</a>
          </div>

          <div className="space-y-3">
            <Row icon={Phone} label="Telefon" value={lead.phone} />
            <Row icon={Mail} label="E-mail" value={lead.email} />
            <Row icon={MessageSquare} label="Probléma / megjegyzés" value={lead.message || "—"} />
          </div>

          <div>
            <label className="block text-sm font-bold text-slate-700 mb-1.5">Státusz módosítása</label>
            <select
              value={lead.status}
              onChange={(e) => onStatus(lead, e.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 font-semibold outline-none focus:border-sky-500"
            >
              {STATUS_OPTIONS.map((s) => (<option key={s.id} value={s.id}>{s.label}</option>))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-bold text-slate-700 mb-1.5">Saját jegyzet</label>
            <textarea
              data-testid="admin-note-input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder="pl. Visszahívni holnap délelőtt…"
              className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 outline-none focus:border-sky-500 resize-none"
            />
            <button onClick={() => onNote(lead, note)} className="mt-2 w-full bg-slate-900 hover:bg-slate-800 text-white font-bold py-2.5 rounded-xl">Jegyzet mentése</button>
          </div>

          <button onClick={() => onDelete(lead)} className="w-full inline-flex items-center justify-center gap-2 text-red-600 hover:bg-red-50 font-bold py-3 rounded-xl border border-red-100">
            <Trash2 className="w-4 h-4" /> Lead törlése
          </button>
        </div>
      </motion.div>
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex gap-3">
      <span className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center shrink-0"><Icon className="w-4 h-4 text-slate-500" /></span>
      <div>
        <div className="text-xs font-bold text-slate-400 uppercase tracking-wide">{label}</div>
        <div className="text-slate-800">{value}</div>
      </div>
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-8 h-8 animate-spin text-sky-600" /></div>;
  if (!user) return <Login />;
  return <Dashboard />;
}
